import { Box, Card, CardActionArea, CardHeader, Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Avatar from "../../components/avatar";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { alpha } from "@mui/material";
import { MailboxDetails } from "../../entities";
import { useState } from "react";

type MailboxGridProp = {
  mailboxes: MailboxDetails[];
  showAllMailboxes: boolean;
  onExpanded: () => void;
};

/**
 * Grid view of mailboxes of a group.
 *
 * @param mailboxes list of mailboxes of the group
 * @param showAllMailboxes boolean flag which informs this component whether to show
 * truncated mailboxes grid view or all mailboxes in grid view.
 * @param onExpanded callback to handle the click event on `Show more` component
 *
 * @returns a JSX element consisting of mailboxes in a grid view,
 */
export const MailboxGrid = ({
  mailboxes,
  showAllMailboxes,
  onExpanded,
}: MailboxGridProp) => {
  const navigate = useNavigate();

  const isTruncatedView = showAllMailboxes === false;

  return (
    <div className="relative w-full">
      <Grid container spacing={2} className="relative">
        {mailboxes.map((mailbox, index) => {
          const shouldSkipTab = isTruncatedView && index >= 6;
          return (
            <Grid item xs={12} sm={6} key={mailbox.uid}>
              <Box className="animate-fade-in-up transition-all duration-300">
                <CardActionArea
                  href={`/mailbox/${mailbox.vanityLink || mailbox.uid}`}
                  disableRipple
                  disableTouchRipple
                  onClick={(e: React.MouseEvent) => {
                    // Prevent navigation if user triess to open mailbox in a new tab
                    if (e.metaKey || e.ctrlKey || e.shiftKey) {
                      return;
                    }
                    // Prevent browser’s full page navigation
                    e.preventDefault();
                    navigate(`/mailbox/${mailbox.vanityLink || mailbox.uid}`);
                  }}
                  className="w-full"
                  tabIndex={shouldSkipTab ? -1 : 0}
                  sx={{
                    borderRadius: "14px",
                    display: "block",
                    mx: { xs: 0, sm: "auto" },
                    transition: "transform 0.2s, box-shadow 0.1s, outline 0.1s",
                    color: "transparent",
                    "&.Mui-focusVisible": {
                      borderRadius: "14px",
                      outline: `1px solid var(--primary-color)`,
                      color: "var(--primary-color)",
                    },

                    "&:hover": {
                      outline: "none",
                    },
                    "&:active": {
                      transform: "scale(0.95)",
                    },
                  }}
                >
                  <Card
                    variant="outlined"
                    elevation={0}
                    className="mx-auto h-[53px] w-full select-none sm:mx-0"
                    sx={{
                      boxShadow: "0px 0px 6px rgba(0,0,0,0.12)",
                      cursor: "pointer",
                      transition:
                        "transform 0.2s, box-shadow 0.2s, background-color 0.3s",
                      borderRadius: "14px",
                      "& .MuiCardHeader-action": {
                        backgroundColor: "#CCCCCC",
                        borderRadius: "100px",
                        transition: "background-color 0.35s ease-in-out",
                      },
                      "&:hover": (theme) => ({
                        boxShadow: "0px 5px 12px rgba(0,0,0,0.15)",
                        backgroundColor: alpha(theme.palette.primary.main, 0.2),
                        transform: "scale(1.02)",
                        "& .MuiCardHeader-action": {
                          backgroundColor: "var(--primary-color)",
                        },
                      }),
                      "&:active": {
                        transform: "scale(0.95)",
                      },
                    }}
                  >
                    <CardHeader
                      sx={{
                        height: "100%",
                        paddingY: 0,
                        display: "flex",
                        alignItems: "center",
                        "& .MuiCardHeader-avatar": {
                          marginRight: 1,
                          marginLeft: -0.7,
                        },
                        "& .MuiCardHeader-action": {
                          alignSelf: "center",
                        },
                      }}
                      title={
                        <div className="w-32 truncate text-sm">
                          {mailbox.name}
                        </div>
                      }
                      subheader={
                        <div className="w-32 truncate pl-[1px] text-[10px]">
                          {mailbox.email}
                        </div>
                      }
                      avatar={
                        <Avatar
                          isAnonymous={false}
                          size={32}
                          uid={mailbox.uid}
                          name={mailbox.name}
                          initialsFontSize={14}
                          bgColor="#CCCCCC"
                          classname="shadow-[0px_2px_6px_rgba(0,0,0,0.12)]"
                        />
                      }
                      action={
                        <div className="flex h-[27px] w-[27px] flex-col items-center justify-center rounded-full">
                          <ArrowForwardIcon className="text-white " />
                        </div>
                      }
                    />
                  </Card>
                </CardActionArea>
              </Box>
            </Grid>
          );
        })}
      </Grid>
      {isTruncatedView && <ShowMoreDivider onExpanded={onExpanded} />}
    </div>
  );
};

type ShowMoreDividerProp = {
  onExpanded: () => void;
};

const ShowMoreDivider = ({ onExpanded }: ShowMoreDividerProp) => {
  const { t } = useTranslation();
  const [isFocused, setFocused] = useState<boolean>(false);
  const [isHovered, setHovered] = useState<boolean>(false);

  const primaryColor = "var(--primary-color)";

  const continerStyle = {
    border: "1px solid transparent",
    transition: "all 0.2s",
    borderColor: isFocused ? primaryColor : "transparent",
    outline: "none",
  };

  const dividerStyle = {
    transition: "all 0.2s",
    borderColor: isHovered ? primaryColor : "black",
    backgroundColor: isHovered ? primaryColor : "#999999",
  };

  const textStyle = {
    transition: "all 0.2s",
    color: isHovered ? primaryColor : "#999999",
  };

  return (
    <div
      className="absolute -bottom-9 left-0 flex h-24 w-full 
          flex-col items-center justify-end bg-gradient-to-t
          from-white via-white/100 to-transparent pb-2
          "
      aria-label={t("Show more mailboxes")}
    >
      <div
        tabIndex={0}
        onKeyUp={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            onExpanded();
          }
        }}
        onFocus={(e) => {
          if ((e.target as HTMLElement).matches(":focus-visible")) {
            setFocused(true);
          }
        }}
        onBlur={() => setFocused(false)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={onExpanded}
        className="z-30 flex w-full cursor-pointer flex-row items-center"
        style={continerStyle}
      >
        <div className="h-px grow" style={dividerStyle} />
        <div className="w-3" />
        <div className="select-none text-sm" style={textStyle}>
          {t(`Show more`)}
        </div>
        <div className="w-3" />
        <div className="h-px grow" style={dividerStyle} />
      </div>
    </div>
  );
};
